import React, { useState } from 'react';
import { X, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsletterModal: React.FC<NewsletterModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post('/api/v1/auth/subscribe', { email });
      
      setIsSuccess(true);
      toast.success('Successfully subscribed to newsletter!');
      
      setTimeout(() => {
        onClose();
        setEmail('');
        setIsSuccess(false);
      }, 2000);
      
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to subscribe';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-playfair text-2xl font-bold text-primary-900">
            Stay Informed
          </h2>
          <button
            onClick={onClose}
            className="text-primary-500 hover:text-primary-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {isSuccess ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="font-playfair text-xl font-bold text-primary-900 mb-2">
              Welcome to Unbiased News!
            </h3>
            <p className="text-primary-700">
              You'll receive our daily digest of high-impact news with zero noise.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-primary-700 mb-4">
                Get our daily digest of the world's most impactful news. 
                AI-curated, bias-analyzed, and noise-free.
              </p>
              
              <div className="bg-primary-50 border border-primary-200 p-4 mb-4">
                <h4 className="font-semibold text-primary-900 mb-2">
                  What you'll receive:
                </h4>
                <ul className="text-sm text-primary-700 space-y-1">
                  <li>• 6-12 high-impact stories daily</li>
                  <li>• Bias score and impact analysis</li>
                  <li>• Deep context and global implications</li>
                  <li>• Zero celebrity or sports noise</li>
                </ul>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="form-input pl-12"
                    required
                  />
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary-400" />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Subscribing...' : 'Subscribe to Newsletter'}
              </button>
            </form>

            <p className="text-xs text-primary-500 mt-4 text-center">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterModal;